import { ReactNode } from 'react';
import { SerializedStyles, css } from '@emotion/react';
import { LocationFeatureFlagSettingName } from '@weave/schema-gen-ts/dist/schemas/payments/messages/location_settings/location_settings.pb';
import { InvoiceSummary as InvoiceSummaryModel } from '@frontend/api-invoices';
import { useTranslation } from '@frontend/i18n';
import { useLocationFeatureSettingsDirect } from '@frontend/payments-hooks';
import { SummaryItem } from '../../SummaryItem';

export interface InvoiceSummaryCardProps {
  summary?: InvoiceSummaryModel;
  loading: boolean;
  style?: SerializedStyles;
  children?: ReactNode;
  locationIds: string[];
}

export type SummaryCardProps = Omit<InvoiceSummaryCardProps, 'style'> & {
  displayDateLabel?: string;
};

export const InvoiceSummaryCard = ({ summary, loading, style, locationIds }: InvoiceSummaryCardProps) => {
  const { t } = useTranslation('payments');
  const { enabledLocations: featureSettingEnabledLocations } = useLocationFeatureSettingsDirect(
    LocationFeatureFlagSettingName.SURCHARGING_ENABLED,
    { locationIds }
  );
  return (
    <div
      className='invoice-summary-card'
      css={[
        css`
          display: flex;
          flex-direction: row;
          justify-content: space-between;
          max-width: 720px;
          flex-wrap: wrap;
        `,
        style,
      ]}
    >
      <SummaryItem
        label={t('Amount Processed')}
        amount={summary?.gross}
        loading={loading}
        infoText={t('Total amount processed by your account. Active report filters will impact the displayed data.')}
        data-trackingid='pay-portal-invoices-btn-amountprocessedhint'
      />
      {featureSettingEnabledLocations.length > 0 && (
        <>
          <SummaryItem
            label={t('Surcharge Amount')}
            amount={summary?.totalSurchargeAmount ?? 0}
            loading={loading}
            infoText={t(
              'Total amount of surcharge collected by your account. Active report filters will impact displayed data.'
            )}
            data-trackingid='pay-portal-invoices-btn-surchargehint'
          />
          <SummaryItem
            label={t('Paid Amount')}
            amount={(summary?.totalSurchargeAmount ?? 0) + (summary?.gross ?? 0)}
            loading={loading}
            infoText={t(
              'Total amount paid to your account (Amount Processed + Surcharge Amount). Active report filters will impact displayed data.'
            )}
            data-trackingid='pay-portal-invoices-btn-paidamounthint'
          />
        </>
      )}

      <SummaryItem
        label={t('Refunds')}
        amount={summary?.refunds}
        loading={loading}
        infoText={t(
          'Total amount of refunds collected by your account. Active report filters will impact displayed data.'
        )}
        data-trackingid='pay-portal-invoices-btn-refundshint'
      />
      <SummaryItem
        label={t('Fees')}
        amount={summary?.fees}
        loading={loading}
        infoText={t('Total amount of fees charged. Active report filters will impact displayed data.')}
        data-trackingid='pay-portal-invoices-btn-feeshint'
      />
      <SummaryItem
        label={t('Net')}
        amount={(summary?.net || 0) + (summary?.totalSurchargeAmount || 0)}
        loading={loading}
        infoText={t('Total amount of net revenue after fees. Active report filters will impact displayed data.')}
        data-trackingid='pay-portal-invoices-btn-nethint'
      />
    </div>
  );
};
